<template>
	<ModalBox
		v-model="isOpen"
		size="lg"
		scroll-body
		hide-close
		header-class="!hidden"
		wrapper-classes="lg:max-h-[90vh]"
		body-classes="!px-0 !py-0"
	>
		<div class="flex flex-col" data-testid="analytics-content-modal">
			<div class="flex items-start justify-between gap-4 px-6 pt-6">
				<div>
					<h3 class="mb-0 text-lg font-semibold text-surface-text-2">Choose content</h3>
					<p class="mb-0 mt-1 text-sm text-surface-8">Select up to {{ maxSelected }} items. Analytics compare the items you pick here.</p>
				</div>
				<button type="button" class="flex size-8 shrink-0 items-center justify-center rounded-md text-surface-7 hover:bg-surface-3 hover:text-white" aria-label="Close" @click="dismiss">
					<icon-base :width="14" :height="14" width-view-box="16" height-view-box="16"><icon-cross /></icon-base>
				</button>
			</div>

			<div class="px-6 pt-4">
				<TabGroup type="segmented" :cols="2" custom-classes="w-full sm:w-72">
					<TabItem :label="`Live streams${liveSelected.length ? ` (${liveSelected.length})` : ''}`" :is-active="activeTab === 'live'" @click="activeTab = 'live'" />
					<TabItem :label="`Videos${vodSelected.length ? ` (${vodSelected.length})` : ''}`" :is-active="activeTab === 'vod'" @click="activeTab = 'vod'" />
				</TabGroup>
			</div>

			<!-- Live streams -->
			<div v-show="activeTab === 'live'" class="px-6 pt-4">
				<div class="relative">
					<FormInput v-model="search" placeholder="Search live streams" custom-classes="!bg-surface-3 !border-0 !pr-10" enterkeyhint="search" />
					<icon-base class="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-surface-7"><icon-search /></icon-base>
				</div>

				<div v-if="!liveStreams.length" class="py-12 text-center">
					<icon-base class="mx-auto text-surface-6" :width="41" :height="40" width-view-box="41" height-view-box="40" fill="none"><icon-empty-live-streams /></icon-base>
					<h4 class="mb-1 mt-3 text-sm font-medium text-surface-text-2">No live streams yet</h4>
					<p class="mb-3 text-xs text-surface-7">Create a live stream to track its analytics.</p>
					<FormButton type="main" size="md" to="/dashboard">Go to live streams</FormButton>
				</div>
				<div v-else-if="!filteredLive.length" class="py-10 text-center text-sm text-surface-7">No live streams match "{{ search }}".</div>
				<ul v-else class="mt-3 divide-y divide-white/[0.04] overflow-hidden rounded-lg bg-surface-3/40">
					<li v-for="stream in filteredLive" :key="stream.key" class="flex min-h-12 items-center px-4 hover:bg-surface-3" :class="{ 'opacity-50': isDisabled(stream.key) }">
						<FormCheckbox
							v-model="liveSelected"
							:input-value="stream.key"
							:disabled="isDisabled(stream.key)"
							name="analyticsLiveStreams"
							class="w-full"
							size="md"
						>
							<template #label>
								<span class="flex w-full items-center gap-3 py-2">
									<span class="size-1.5 shrink-0 rounded-full" :class="stream.enabled ? 'bg-success' : 'bg-surface-6'" aria-hidden="true" />
									<span class="min-w-0 flex-1 truncate text-sm text-surface-text-1" :title="stream.name">{{ stream.name }}</span>
								</span>
							</template>
						</FormCheckbox>
					</li>
				</ul>
			</div>

			<!-- Videos -->
			<div v-show="activeTab === 'vod'" class="px-6 pt-4">
				<div v-if="!vodFolders.length" class="py-12 text-center">
					<icon-base class="mx-auto text-surface-6" :width="40" :height="40" width-view-box="40" height-view-box="40" fill="none"><icon-empty-vod-files /></icon-base>
					<h4 class="mb-1 mt-3 text-sm font-medium text-surface-text-2">No videos yet</h4>
					<p class="mb-3 text-xs text-surface-7">Upload videos in Video Hosting to track their analytics.</p>
					<FormButton type="main" size="md" to="/vods">Go to Video Hosting</FormButton>
				</div>

				<template v-else-if="!activeFolder">
					<div class="relative">
						<FormInput v-model="search" placeholder="Search folders" custom-classes="!bg-surface-3 !border-0 !pr-10" enterkeyhint="search" />
						<icon-base class="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-surface-7"><icon-search /></icon-base>
					</div>
					<div class="mt-3 grid grid-cols-[1fr_auto_auto] gap-x-4 px-4 pb-1 text-[11px] uppercase tracking-wide text-surface-7">
						<span>Folder</span><span class="w-12 text-right">Files</span><span class="w-16 text-right">Size</span>
					</div>
					<ul class="divide-y divide-white/[0.04] overflow-hidden rounded-lg bg-surface-3/40">
						<li v-for="folder in filteredFolders" :key="folder._id">
							<button type="button" class="grid w-full grid-cols-[1fr_auto_auto] items-center gap-x-4 px-4 py-3 text-left hover:bg-surface-3" @click="openFolder(folder)">
								<span class="flex min-w-0 items-center gap-2">
									<img src="@/assets/images/solid_folder.svg" alt="" class="size-4 shrink-0">
									<span class="truncate text-sm text-surface-text-1">{{ folder.name }}</span>
									<Badge v-if="folder.liveParent" inline size="xs" type="main" class="shrink-0">Live-to-VOD</Badge>
								</span>
								<span class="w-12 text-right text-xs tabular-nums text-surface-8">{{ folder.totalFiles ?? '' }}</span>
								<span class="w-16 text-right text-xs tabular-nums text-surface-8">{{ formatBytes(folder.totalBytes) }}</span>
							</button>
						</li>
						<li v-if="!filteredFolders.length" class="px-4 py-8 text-center text-sm text-surface-7">No folders match "{{ search }}".</li>
					</ul>
				</template>

				<template v-else>
					<div class="flex flex-wrap items-center gap-2">
						<button type="button" class="inline-flex h-8 items-center gap-1.5 rounded-md px-2 text-sm text-surface-8 hover:bg-surface-3 hover:text-white" @click="folderBack">
							<icon-base :width="12" :height="12" width-view-box="16" height-view-box="16"><icon-chevron-left /></icon-base>
							Back
						</button>
						<span class="flex min-w-0 items-center gap-2 text-sm text-surface-text-2">
							<img src="@/assets/images/solid_folder.svg" alt="" class="size-4 shrink-0">
							<span class="truncate">{{ activeFolder.name }}<template v-if="activeSubfolder"> / {{ activeSubfolder.name }}</template></span>
						</span>
						<label v-if="folderTargets.length" class="ml-auto flex cursor-pointer items-center gap-2 text-xs text-surface-8">
							<span>Select this folder</span>
							<FormCheckbox :model-value="isFolderFullySelected" @change="toggleFolder" />
						</label>
					</div>

					<div v-if="folderLoading" class="flex items-center justify-center py-12 text-sm text-surface-7">
						<clip-loader color="var(--c-dark-8)" size="14px" class="mr-2" /> Loading videos
					</div>
					<template v-else>
						<ul v-if="subfolderRows.length" class="mt-3 divide-y divide-white/[0.04] overflow-hidden rounded-lg bg-surface-3/40">
							<li v-for="sf in subfolderRows" :key="sf._id">
								<button type="button" class="flex w-full items-center gap-2 px-4 py-3 text-left hover:bg-surface-3" @click="activeSubfolder = sf">
									<img src="@/assets/images/solid_folder.svg" alt="" class="size-4 shrink-0">
									<span class="min-w-0 flex-1 truncate text-sm text-surface-text-1">{{ sf.name }}</span>
									<span class="text-xs tabular-nums text-surface-8">{{ sf.fileCount }}</span>
								</button>
							</li>
						</ul>
						<ul v-if="visibleFiles.length" class="mt-3 divide-y divide-white/[0.04] overflow-hidden rounded-lg bg-surface-3/40">
							<li v-for="file in visibleFiles" :key="vodFileKey(file)" class="flex min-h-12 items-center px-4 hover:bg-surface-3" :class="{ 'opacity-50': isDisabled(vodFileKey(file)) }">
								<FormCheckbox v-model="vodSelected" :input-value="vodFileKey(file)" :disabled="isDisabled(vodFileKey(file))" name="analyticsVodFiles" class="w-full" @update:model-value="rememberFiles">
									<template #label>
										<span class="grid w-full grid-cols-[1fr_auto] items-center gap-x-4 py-2 sm:grid-cols-[1fr_5rem_7rem]">
											<span class="min-w-0">
												<span class="block truncate text-sm text-surface-text-1" :title="file.fileName">{{ file.fileName }}</span>
												<span v-if="file.mediaInfo?.width" class="block text-[11px] text-surface-7">{{ file.mediaInfo.width }} x {{ file.mediaInfo.height }}</span>
											</span>
											<span class="text-right text-xs tabular-nums text-surface-8">{{ formatDuration(file.mediaInfo?.duration) }}</span>
											<span class="hidden text-right text-xs tabular-nums text-surface-8 sm:block">{{ formatDate(file.creationTime) }}</span>
										</span>
									</template>
								</FormCheckbox>
							</li>
						</ul>
						<p v-if="!visibleFiles.length && !subfolderRows.length" class="py-10 text-center text-sm text-surface-7">This folder has no videos.</p>
					</template>
				</template>
			</div>
		</div>

		<template #footer>
			<div class="flex flex-col gap-3 border-t border-surface-3 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
				<div class="text-xs" :class="totalSelected >= maxSelected ? 'text-warning' : 'text-surface-7'">
					<span class="tabular-nums text-surface-text-1">{{ totalSelected }}</span> of {{ maxSelected }} selected<template v-if="totalSelected >= maxSelected">. Remove an item to add another.</template>
				</div>
				<div class="flex flex-wrap justify-end gap-2">
					<FormButton type="secondary" size="md" @click="dismiss">Cancel</FormButton>
					<FormButton v-if="totalSelected" type="danger" size="md" @click="clearSelection">Clear</FormButton>
					<FormButton type="main" size="md" :disabled="!totalSelected || totalSelected > maxSelected" @click="apply">
						Apply<template v-if="totalSelected"> ({{ totalSelected }})</template>
					</FormButton>
				</div>
			</div>
		</template>
	</ModalBox>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import ModalBox from '@/components/modals/ModalBox.vue';
import ClipLoader from '@/components/ui/ClipLoader.vue';
import StreamService from '@/services/StreamService';
import FormButton from '@/components/Atoms/FormButton.vue';
import FormInput from '@/components/Atoms/FormInput.vue';
import FormCheckbox from '@/components/Atoms/FormCheckbox.vue';
import TabGroup from '@/components/Atoms/TabGroup.vue';
import TabItem from '@/components/Atoms/TabItem.vue';
import Badge from '@/components/Badge.vue';
import IconBase from '@/components/icon/IconBase.vue';
import IconSearch from '@/components/icon/IconSearch.vue';
import IconCross from '@/components/icon/IconCross.vue';
import IconChevronLeft from '@/components/icon/IconChevronLeft.vue';
import IconEmptyLiveStreams from '@/components/icon/IconEmptyLiveStreams.vue';
import IconEmptyVodFiles from '@/components/icon/IconEmptyVodFiles.vue';
import { vodFileKey } from '@/components/Analytics/analyticsContentIndex';
import { MAX_SELECTED_CONTENT } from '@/components/Analytics/analyticsMetrics';

const props = defineProps({
	modelValue: { type: Boolean, default: false },
	// Current selection (stream keys / VOD refIds) used to seed the modal.
	live: { type: Array, default: () => [] },
	vod: { type: Array, default: () => [] },
	maxSelected: { type: Number, default: MAX_SELECTED_CONTENT },
});

const emit = defineEmits(['update:modelValue', 'apply']);

const store = useStore();

const isOpen = computed({
	get: () => props.modelValue,
	set: (v) => emit('update:modelValue', v),
});

const activeTab = ref('live');
const search = ref('');
const liveSelected = ref([]);
const vodSelected = ref([]);
// VOD files seen while browsing, so the dashboard can label selected refIds.
const knownFiles = ref(new Map());

const activeFolder = ref(null);
const activeSubfolder = ref(null);
const folderFiles = ref([]);
const folderSubfolders = ref([]);
const folderLoading = ref(false);

const liveStreams = computed(() => store.getters['Streams/allInOneStreams'] || []);
const vodFolders = computed(() => store.getters['Streams/vodStreams'] || []);

const filteredLive = computed(() => {
	const q = search.value.trim().toLowerCase();
	const list = q ? liveStreams.value.filter((s) => (s.name || '').toLowerCase().includes(q)) : liveStreams.value;
	return list.slice().sort((a, b) => new Date(b.creationTime || 0) - new Date(a.creationTime || 0));
});

const filteredFolders = computed(() => {
	const q = search.value.trim().toLowerCase();
	return q ? vodFolders.value.filter((f) => (f.name || '').toLowerCase().includes(q)) : vodFolders.value;
});

const totalSelected = computed(() => liveSelected.value.length + vodSelected.value.length);

function isDisabled(key) {
	if (totalSelected.value < props.maxSelected) return false;
	return !liveSelected.value.includes(key) && !vodSelected.value.includes(key);
}

const visibleFiles = computed(() => {
	const subfolderIds = new Set(folderSubfolders.value.map((s) => s._id));
	if (activeSubfolder.value) return folderFiles.value.filter((f) => f.subfolder === activeSubfolder.value._id);
	return folderFiles.value.filter((f) => !f.subfolder || !subfolderIds.has(f.subfolder));
});

const subfolderRows = computed(() => {
	if (activeSubfolder.value) return [];
	return folderSubfolders.value.map((s) => ({ ...s, fileCount: folderFiles.value.filter((f) => f.subfolder === s._id).length }));
});

const folderTargets = computed(() => (activeSubfolder.value ? visibleFiles.value : folderFiles.value));
const isFolderFullySelected = computed(() => folderTargets.value.length > 0 && folderTargets.value.every((f) => vodSelected.value.includes(vodFileKey(f))));

function rememberFiles() {
	for (const f of folderFiles.value) {
		knownFiles.value.set(vodFileKey(f), { ...f, playlistName: activeFolder.value?.name, playlistId: activeFolder.value?._id });
	}
}

function toggleFolder() {
	const keys = folderTargets.value.map(vodFileKey);
	if (isFolderFullySelected.value) {
		vodSelected.value = vodSelected.value.filter((k) => !keys.includes(k));
	} else {
		const room = Math.max(0, props.maxSelected - liveSelected.value.length);
		vodSelected.value = [...new Set([...vodSelected.value, ...keys])].slice(0, room);
	}
	rememberFiles();
}

async function openFolder(folder) {
	activeFolder.value = folder;
	activeSubfolder.value = null;
	folderLoading.value = true;
	search.value = '';
	try {
		folderFiles.value = (await StreamService.getStreamPlaylist(folder._id)) || [];
		folderSubfolders.value = folder.liveParent ? [] : (await StreamService.getVodSubfolders(folder._id).catch(() => [])) || [];
		rememberFiles();
	} catch (e) {
		folderFiles.value = [];
		folderSubfolders.value = [];
	} finally {
		folderLoading.value = false;
	}
}

function folderBack() {
	if (activeSubfolder.value) activeSubfolder.value = null;
	else activeFolder.value = null;
}

function formatBytes(b) {
	if (!b || b <= 0) return '0 MB';
	const i = Math.floor(Math.log(b) / Math.log(1000));
	const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
	return `${(b / Math.pow(1000, i)).toFixed(i ? 1 : 0)} ${sizes[i] || 'PB'}`;
}

function formatDuration(seconds) {
	const s = Math.round(Number(seconds) || 0);
	if (!s) return '';
	const h = Math.floor(s / 3600);
	const m = Math.floor((s % 3600) / 60);
	const sec = s % 60;
	if (h) return `${h}h ${m}m`;
	if (m) return `${m}m ${sec}s`;
	return `${sec}s`;
}

const dateFormatter = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
function formatDate(d) {
	if (!d) return '';
	const date = new Date(d);
	return Number.isNaN(date.getTime()) ? '' : dateFormatter.format(date);
}

function reset() {
	liveSelected.value = props.live.slice();
	vodSelected.value = props.vod.slice();
	search.value = '';
	activeFolder.value = null;
	activeSubfolder.value = null;
	activeTab.value = props.live.length || !props.vod.length ? 'live' : 'vod';
}

watch(() => props.modelValue, (open) => {
	if (open) {
		reset();
		store.dispatch('Streams/getStreams');
	}
});

function dismiss() {
	isOpen.value = false;
}

function clearSelection() {
	liveSelected.value = [];
	vodSelected.value = [];
}

function apply() {
	if (!totalSelected.value || totalSelected.value > props.maxSelected) return;
	emit('apply', {
		live: liveSelected.value.slice(),
		vod: vodSelected.value.slice(),
		vodFiles: vodSelected.value.map((k) => knownFiles.value.get(k)).filter(Boolean),
	});
	dismiss();
}
</script>
